// src/pages/Leave.jsx
import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { notifyUser, notifyRoles } from "../utils/notifications";
import { getProbationInfo } from "../utils/probation";
import {
  FaCalendarAlt,
  FaCheck,
  FaTimes,
  FaClock,
  FaPlus,
  FaHistory,
  FaUsers,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaLock,
  FaUserClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import styles from "./Leave.module.css";

// ── Leave type definitions — mirrors Offer Letter Section 8 ─────────────────
const LEAVE_TYPES = {
  EL: {
    label: "Earned Leave (EL / PL)",
    short: "EL",
    defaultDays: 12, // offer-letter minimum
    maxDays: 15,     // offer-letter maximum
    color: "#3b82f6",
    desc: "Accrued monthly · Shops & Establishments Act",
    carryForward: "yes",
    carryMax: 30,
    onSeparation: "encashed",
  },
  CL: {
    label: "Casual Leave (CL)",
    short: "CL",
    defaultDays: 6,
    maxDays: 8,
    color: "#8b5cf6",
    desc: "Non-encashable, non-carry-forward",
    carryForward: "no",
    onSeparation: "lapses",
  },
  SL: {
    label: "Sick Leave (SL)",
    short: "SL",
    defaultDays: 6,
    maxDays: 8,
    color: "#f59e0b",
    desc: "Medical certificate required for >2 consecutive days",
    carryForward: "conditional",
    carryMax: 45,
    onSeparation: "lapses",
  },
  ML: {
    label: "Maternity Leave",
    short: "ML",
    defaultDays: 182,
    maxDays: 182,
    color: "#ec4899",
    desc: "Maternity Benefit (Amendment) Act, 2017",
    carryForward: "na",
    onSeparation: "na",
  },
  PL: {
    label: "Paternity Leave",
    short: "PL",
    defaultDays: 3,
    maxDays: 5,
    color: "#06b6d4",
    desc: "Subject to HR approval",
    carryForward: "no",
    onSeparation: "lapses",
  },
  BL: {
    label: "Bereavement Leave",
    short: "BL",
    defaultDays: 2,
    maxDays: 3,
    color: "#6b7280",
    desc: "Immediate family only · Per event",
    carryForward: "no",
    onSeparation: "lapses",
  },
  UL: {
    label: "Unpaid Leave",
    short: "UL",
    defaultDays: null,
    maxDays: null,
    color: "#ef4444",
    desc: "Salary deducted at Basic ÷ 26 per day · No prior balance needed",
    carryForward: "na",
    onSeparation: "na",
  },
};

const LEAVE_KEYS = Object.keys(LEAVE_TYPES);

// Leave types where exceeding balance is allowed (special / exceptional)
const BALANCE_EXEMPT = ["ML", "PL", "BL", "UL"];

const STATUS_META = {
  pending: { bg: "#fef3c7", color: "#92400e" },
  approved: { bg: "#d1fae5", color: "#065f46" },
  rejected: { bg: "#fee2e2", color: "#991b1b" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const balDocId = (uid, year) => `${uid}_${year}`;

const makeDefaultBalance = (uid, year) => {
  const doc = { uid, year };
  LEAVE_KEYS.forEach((k) => {
    doc[k] = { total: LEAVE_TYPES[k].defaultDays, taken: 0, pending: 0 };
  });
  return doc;
};

const countDays = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
};

const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

// ── Sub-components ───────────────────────────────────────────────────────────
const LeaveTypePill = ({ type }) => {
  const t = LEAVE_TYPES[type];
  if (!t) return <span>{type}</span>;
  return (
    <span
      style={{
        background: t.color + "22",
        color: t.color,
        border: `1px solid ${t.color}55`,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: "0.73rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {t.short}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUS_META[status] ?? STATUS_META.pending;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: "0.73rem",
        fontWeight: 700,
        textTransform: "capitalize",
        display: "inline-block",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
};

// ── PolicySection accordion item ─────────────────────────────────────────────
const PolicySection = ({ title, children, highlight }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${styles.policySection} ${highlight ? styles.policySectionHighlight : ""}`}>
      <button className={styles.policySectionHeader} onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        {open ? <FaChevronUp /> : <FaChevronDown />}
      </button>
      {open && <div className={styles.policySectionBody}>{children}</div>}
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const Leave = ({ selectedEmployee } = {}) => {
  const { userData } = useAuth();
  const canManage =
    userData?.role === "admin" || userData?.role === "superadmin";

  // When admin opens a specific employee's leaves, view that employee's data.
  // viewUser = the person whose leaves are being shown.
  // userData  = the logged-in user (used only for role/permission checks).
  const viewUser = selectedEmployee || userData;
  const viewUid  = viewUser?.uid || viewUser?.id;
  const selectedUid = selectedEmployee?.uid || selectedEmployee?.id;
  const isViewingOther = selectedEmployee && selectedUid !== userData?.uid;

  const currentYear = new Date().getFullYear();

  const [myBalance, setMyBalance] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]); // admin view
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("balance");
  const [adminFilter, setAdminFilter] = useState("pending");
  const [submitting, setSubmitting] = useState(false);
  const [allBalances, setAllBalances] = useState([]); // superadmin / delegated manage
  const [editingBalance, setEditingBalance] = useState(null);
  const [savingBalance, setSavingBalance] = useState(false);
  const [allUsers, setAllUsers] = useState([]);           // superadmin access control
  const [settingMinimums, setSettingMinimums] = useState(false);
  const [togglingAccess, setTogglingAccess] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [accessSearch, setAccessSearch] = useState("");
  const [accessStatusFilter, setAccessStatusFilter] = useState("all");

  // Reset per-employee state whenever the viewed user switches
  useEffect(() => {
    setMyBalance(null);
    setMyLeaves([]);
    setLoading(true);
    setActiveTab("balance");
  }, [viewUid]);

  const [form, setForm] = useState({
    type: "CL",
    startDate: todayStr(),
    endDate: todayStr(),
    reason: "",
    halfDay: false,
    proofType: "",
    proofNote: "",
  });

  // ── Probation period logic ────────────────────────────────────────────────
  // Base probation = 3 months from joiningDate.
  // If superadmin extended probation, add another 3 months on top.
  // Uses viewUser so admins viewing an employee also see the correct state.
  const [extendingProbation, setExtendingProbation] = useState(false);

  const probationInfo = React.useMemo(() => {
    const joining = viewUser?.joiningDate; // "YYYY-MM-DD"
    if (!joining) return { inProbation: false, endsOn: null, daysLeft: 0, extended: false };

    const info = getProbationInfo(joining, viewUser?.probationExtended);
    if (!info.joinDate) return { inProbation: false, endsOn: null, daysLeft: 0, extended: false };
    const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

    return {
      inProbation: info.inProbation,
      endsOn: fmt(info.endDate),
      daysLeft: info.daysLeft,
      extended: info.extended,
      joinDate: fmt(info.joinDate),
    };
  }, [viewUser?.joiningDate, viewUser?.probationExtended]);

  const handleExtendProbation = async () => {
    if (!viewUid) return;
    const targetName = viewUser?.name || "this employee";
    if (!window.confirm(
      `Extend probation period for ${targetName} by 3 more months (total 6 months from joining)?\n\nThis will disable leave application for an additional 3 months.`
    )) return;

    setExtendingProbation(true);
    try {
      await updateDoc(doc(db, "users", viewUid), { probationExtended: true });
      toast.success(`Probation period extended for ${targetName}.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to extend probation period.");
    }
    setExtendingProbation(false);
  };

  const handleRevokeProbationExtension = async () => {
    if (!viewUid) return;
    const targetName = viewUser?.name || "this employee";
    if (!window.confirm(`Revoke probation extension for ${targetName}? Their probation will revert to 3 months from joining date.`)) return;

    setExtendingProbation(true);
    try {
      await updateDoc(doc(db, "users", viewUid), { probationExtended: false });
      toast.success(`Probation extension revoked for ${targetName}.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to revoke probation extension.");
    }
    setExtendingProbation(false);
  };

  // ── Ensure balance doc exists for viewed user ───────────────────────────
  const ensureBalance = useCallback(async () => {
    if (!viewUid) return;
    const ref = doc(db, "leave_balances", balDocId(viewUid, currentYear));
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      const base = makeDefaultBalance(viewUid, currentYear);

      // ── EL carry-forward: look at previous year's balance ──────────────
      // Policy: unused EL carries forward, capped at 30 days total accumulation
      const prevRef = doc(
        db,
        "leave_balances",
        balDocId(viewUid, currentYear - 1)
      );
      const prevSnap = await getDoc(prevRef);
      if (prevSnap.exists()) {
        const prev = prevSnap.data();
        // unused = what was left after all taken (ignore pending — they should be resolved)
        const prevUnused = Math.max(
          0,
          (prev.EL?.total ?? 0) - (prev.EL?.taken ?? 0)
        );
        const carried = Math.min(prevUnused, LEAVE_TYPES.EL.carryMax); // cap at 30
        if (carried > 0) {
          base.EL.total = LEAVE_TYPES.EL.defaultDays + carried;
          base.EL.carriedForward = carried; // stored for audit / display
        }
        // SL carry-forward is state-law dependent; superadmin can adjust manually
        // via Manage Balances if the employee's state mandates it
      }

      await setDoc(ref, {
        ...base,
        createdAt: serverTimestamp(),
      });
    }
  }, [viewUid, currentYear]);

  // ── Realtime: viewed user's balance + leaves ─────────────────────────────
  useEffect(() => {
    if (!viewUid) return;

    ensureBalance();

    const balRef = doc(
      db,
      "leave_balances",
      balDocId(viewUid, currentYear)
    );
    const unsubBal = onSnapshot(balRef, (snap) => {
      if (snap.exists()) setMyBalance(snap.data());
      else setMyBalance(null);
    });

    // NOTE: no orderBy here — compound (where+orderBy) requires a composite index.
    // Sort client-side instead so it works without any index config.
    const myLeavesQ = query(
      collection(db, "leaves"),
      where("uid", "==", viewUid)
    );
    const unsubLeaves = onSnapshot(
      myLeavesQ,
      (snap) => {
        const sorted = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ta = a.appliedAt?.toMillis?.() ?? 0;
            const tb = b.appliedAt?.toMillis?.() ?? 0;
            return tb - ta; // newest first
          });
        setMyLeaves(sorted);
        setLoading(false);
      },
      (err) => { console.error("[Leave] myLeaves query error:", err); setLoading(false); }
    );

    return () => {
      unsubBal();
      unsubLeaves();
    };
  }, [viewUid, currentYear, ensureBalance]);

  // ── Realtime: all leaves (admin only) ────────────────────────────────────
  useEffect(() => {
    if (!canManage) return;

    // NOTE: no orderBy — Firestore excludes docs where the field doesn't exist
    // when using orderBy, which silently hides any leaves without appliedAt.
    // Sort client-side instead so all docs are always returned.
    const allLeavesQ = query(collection(db, "leaves"));
    const unsub = onSnapshot(
      allLeavesQ,
      (snap) => {
        const all = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ta = a.appliedAt?.toMillis?.() ?? 0;
            const tb = b.appliedAt?.toMillis?.() ?? 0;
            return tb - ta; // newest first
          });
        setAllLeaves(all);
      },
      (err) => console.error("[Leave] allLeaves query error:", err)
    );
    return unsub;
  }, [canManage]);

  // ── Realtime: all balances (superadmin or delegated) ────────────────────────
  useEffect(() => {
    if (userData?.role !== "superadmin" && !userData?.manageLeaveAccess) return;
    const q = query(
      collection(db, "leave_balances"),
      where("year", "==", currentYear)
    );
    const unsub = onSnapshot(q, (snap) => {
      setAllBalances(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [userData?.role, userData?.manageLeaveAccess, currentYear]);

  // ── Realtime: all users for access control (superadmin only) ─────────────
  useEffect(() => {
    if (userData?.role !== "superadmin") return;
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setAllUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [userData?.role]);

  // ── Auto-init missing balance docs when allUsers loads (superadmin/delegated) ──
  // Ensures every new employee gets offer-letter minimums automatically,
  // without needing to open the Leave page themselves.
  useEffect(() => {
    if (!allUsers.length || !allBalances.length && allUsers.length === 0) return;
    if (userData?.role !== "superadmin" && !userData?.manageLeaveAccess) return;

    const existingUids = new Set(allBalances.map((b) => b.uid));
    const missing = allUsers.filter(
      (u) => u.role && !existingUids.has(u.id)
    );
    if (missing.length === 0) return;

    // Fire-and-forget: create balance docs with minimums for each missing user
    const batch = writeBatch(db);
    missing.forEach((u) => {
      const ref = doc(db, "leave_balances", balDocId(u.id, currentYear));
      batch.set(ref, {
        ...makeDefaultBalance(u.id, currentYear),
        createdAt: serverTimestamp(),
      });
    });
    batch.commit().catch(console.error);
  }, [allUsers, allBalances, userData?.role, userData?.manageLeaveAccess, currentYear]);

  // ── Submit leave request ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Hard block during probation — belt-and-suspenders guard even if UI is disabled
    if (probationInfo.inProbation) {
      return toast.error(
        `Leave applications are not permitted during the 3-month probation period. Your probation ends on ${probationInfo.endsOn}.`
      );
    }

    if (!form.reason.trim()) return toast.error("Please provide a reason.");

    const days = form.halfDay ? 0.5 : countDays(form.startDate, form.endDate);
    if (days <= 0) return toast.error("Invalid date range.");

    const bal = myBalance?.[form.type] ?? {
      total: LEAVE_TYPES[form.type].defaultDays,
      taken: 0,
      pending: 0,
    };
    const remaining = bal.total - bal.taken - bal.pending;

    if (days > remaining && !BALANCE_EXEMPT.includes(form.type)) {
      return toast.error(
        `Insufficient ${LEAVE_TYPES[form.type].short} balance. Available: ${remaining} day${remaining !== 1 ? "s" : ""}.`
      );
    }

    setSubmitting(true);
    try {
      const batch = writeBatch(db);

      const leaveRef = doc(collection(db, "leaves"));
      batch.set(leaveRef, {
        uid: viewUid,
        userName: viewUser?.name || viewUser?.email || "",
        employeeId: viewUser?.employeeId || "",
        department: viewUser?.department || "",
        applicantRole: viewUser?.role || "employee",
        type: form.type,
        typeLabel: LEAVE_TYPES[form.type].label,
        startDate: form.startDate,
        endDate: form.endDate,
        days,
        halfDay: form.halfDay,
        reason: form.reason.trim(),
        status: "pending",
        appliedAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
        adminNote: "",
        ...(form.type === "UL" && {
          proofType: form.proofType,
          proofNote: form.proofNote.trim(),
        }),
      });

      const balRef = doc(
        db,
        "leave_balances",
        balDocId(viewUid, currentYear)
      );
      batch.update(balRef, {
        [`${form.type}.pending`]: bal.pending + days,
      });

      await batch.commit();
      toast.success("Leave request submitted successfully.");

      // ── Notify managers per hierarchy ──
      // employee → admins + superadmins; admin → superadmin only; superadmin → nobody
      const applicantRole = viewUser?.role || 'employee';
      const rolesToNotify =
        applicantRole === 'superadmin' ? [] :
        applicantRole === 'admin'      ? ['superadmin'] :
                                         ['admin', 'superadmin'];
      if (rolesToNotify.length) {
        notifyRoles({
          roles: rolesToNotify,
          type: 'leave_request',
          title: 'New Leave Request',
          body: `${viewUser?.name || 'An employee'} applied for ${LEAVE_TYPES[form.type]?.label || form.type} (${days} day${days !== 1 ? 's' : ''})`,
          link: '/dashboard/manage',
          excludeUid: userData?.uid,
          senderUid: userData?.uid,
          senderName: userData?.name || '',
          metadata: { leaveId: leaveRef.id, type: form.type, days },
        });
      }

      setForm((f) => ({ ...f, reason: "", halfDay: false, proofType: "", proofNote: "" }));
      setActiveTab("history");
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit leave request.");
    }
    setSubmitting(false);
  };

  // ── Confirmation toast before approve / reject ───────────────────────────
  const confirmReview = (leave, action) => {
    const isApprove = action === "approved";
    const empName = leave.userName || "this employee";
    const leaveType = LEAVE_TYPES[leave.type]?.short || leave.type;

    toast(
      (t) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 240 }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: "0.88rem", color: "#1e293b" }}>
            {isApprove ? "✅ Approve" : "❌ Reject"} leave?
          </p>
          <p style={{ margin: 0, fontSize: "0.78rem", color: "#64748b" }}>
            {empName} · {leaveType} · {leave.days} day{leave.days !== 1 ? "s" : ""}
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button
              onClick={() => { toast.dismiss(t.id); handleReview(leave, action); }}
              style={{
                flex: 1, padding: "6px 0", borderRadius: 6, border: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "0.8rem",
                background: isApprove ? "#16a34a" : "#dc2626", color: "#fff",
              }}
            >
              Yes, {isApprove ? "Approve" : "Reject"}
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                flex: 1, padding: "6px 0", borderRadius: 6, border: "1px solid #cbd5e1",
                cursor: "pointer", fontWeight: 600, fontSize: "0.8rem",
                background: "#f8fafc", color: "#475569",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      { duration: 8000 }
    );
  };

  // ── Approve / Reject (admin) ──────────────────────────────────────────────
  const handleReview = async (leave, action) => {
    if (userData?.role === "admin") {
      // Admin cannot act on their own leave
      if (leave.uid === userData.uid) {
        return toast.error("Admins cannot approve their own leaves. Contact a Superadmin.");
      }
      // Admin cannot act on another admin's or superadmin's leave
      // Use stored applicantRole; fall back to a Firestore fetch for legacy docs
      let applicantRole = leave.applicantRole;
      if (!applicantRole) {
        try {
          const snap = await getDoc(doc(db, "users", leave.uid));
          applicantRole = snap.data()?.role || "employee";
        } catch (_) { applicantRole = "employee"; }
      }
      if (applicantRole === "admin" || applicantRole === "superadmin") {
        return toast.error("Admins can only approve/reject employee leave requests. Admin-level leaves require Superadmin approval.");
      }
    }
    try {
      // Fetch target employee's balance at review time
      const targetBalRef = doc(
        db,
        "leave_balances",
        balDocId(leave.uid, currentYear)
      );
      const targetBalSnap = await getDoc(targetBalRef);
      const targetBal = targetBalSnap.exists()
        ? targetBalSnap.data()[leave.type] ?? { taken: 0, pending: 0 }
        : { taken: 0, pending: 0 };

      const batch = writeBatch(db);

      // ── UL: calculate salary deduction from employee's basic pay ──────
      let deductionAmount = 0;
      if (action === "approved" && leave.type === "UL") {
        const empSnap = await getDoc(doc(db, "users", leave.uid));
        const basicSalary = empSnap.data()?.salary?.basic || 0;
        deductionAmount = basicSalary > 0
          ? Math.round((basicSalary / 26) * leave.days)
          : 0;
      }

      batch.update(doc(db, "leaves", leave.id), {
        status: action,
        reviewedAt: serverTimestamp(),
        reviewedBy: userData?.name || userData?.email,
        ...(leave.type === "UL" && action === "approved" && { deductionAmount }),
      });

      if (action === "approved") {
        batch.update(targetBalRef, {
          [`${leave.type}.taken`]: targetBal.taken + leave.days,
          [`${leave.type}.pending`]: Math.max(
            0,
            targetBal.pending - leave.days
          ),
        });
      } else {
        // rejected — release pending hold
        batch.update(targetBalRef, {
          [`${leave.type}.pending`]: Math.max(
            0,
            targetBal.pending - leave.days
          ),
        });
      }

      await batch.commit();
      toast.success(
        action === "approved" && leave.type === "UL" && deductionAmount > 0
          ? `Unpaid leave approved. Salary deduction: ₹${deductionAmount.toLocaleString("en-IN")}.`
          : `Leave ${action}.`
      );

      // ── Notify the leave applicant of the decision ──
      notifyUser({
        recipientUid: leave.uid,
        type: action === 'approved' ? 'leave_approved' : 'leave_rejected',
        title: `Leave ${action === 'approved' ? 'Approved' : 'Rejected'}`,
        body: `Your ${LEAVE_TYPES[leave.type]?.label || leave.type} (${leave.days} day${leave.days !== 1 ? 's' : ''}) has been ${action} by ${userData?.name || 'admin'}.`,
        link: '/dashboard',
        senderUid: userData?.uid,
        senderName: userData?.name || '',
        metadata: { leaveId: leave.id, type: leave.type, days: leave.days },
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to update leave.");
    }
  };

  // ── Superadmin: reset all employees to offer-letter minimums ───────────────
  const handleSetAllMinimums = async () => {
    if (!window.confirm(`Set ALL employees' leave totals to offer-letter minimums for ${currentYear}? This will overwrite any custom values.`)) return;
    setSettingMinimums(true);
    try {
      const batch = writeBatch(db);
      allBalances.forEach((bal) => {
        const ref = doc(db, "leave_balances", bal.id);
        const updates = {};
        LEAVE_KEYS.filter((k) => LEAVE_TYPES[k].defaultDays !== null).forEach((k) => {
          updates[`${k}.total`] = LEAVE_TYPES[k].defaultDays;
        });
        batch.update(ref, updates);
      });
      await batch.commit();
      toast.success("All employees reset to offer-letter minimum leave balances.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset balances.");
    }
    setSettingMinimums(false);
  };

  // ── Superadmin: grant / revoke manage-balances access ────────────────────
  const handleToggleAccess = async (uid, currentAccess) => {
    setTogglingAccess(uid);
    try {
      await updateDoc(doc(db, "users", uid), { manageLeaveAccess: !currentAccess });
      toast.success(!currentAccess ? "Manage Balances access granted." : "Access revoked.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update access.");
    }
    setTogglingAccess(null);
  };

  // ── Superadmin: update employee leave totals ──────────────────────────────
  const handleUpdateTotal = async () => {
    if (!editingBalance) return;
    setSavingBalance(true);
    try {
      const ref = doc(db, "leave_balances", editingBalance.id);
      const updates = {};
      LEAVE_KEYS.filter((k) => LEAVE_TYPES[k].defaultDays !== null).forEach((k) => {
        updates[`${k}.total`] = Number(editingBalance[`edit_${k}`]);
      });
      await updateDoc(ref, updates);
      toast.success("Leave balances updated successfully.");
      setEditingBalance(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update leave balances.");
    }
    setSavingBalance(false);
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const formDays = form.halfDay ? 0.5 : countDays(form.startDate, form.endDate);

  // Role-scoped leaves: superadmin sees all; admin sees only employee leaves.
  const roleFilteredLeaves =
    userData?.role === "superadmin"
      ? allLeaves
      : allLeaves.filter((l) => !l.applicantRole || l.applicantRole === "employee");

  // When viewing a specific employee from the Management tab, scope "All Requests"
  // to that employee only — not the entire organisation.
  const managementScopedLeaves = isViewingOther
    ? roleFilteredLeaves.filter((l) => l.uid === viewUid)
    : roleFilteredLeaves;

  const pendingCount = managementScopedLeaves.filter((l) => l.status === "pending").length;

  const filteredAll =
    adminFilter === "all"
      ? managementScopedLeaves
      : managementScopedLeaves.filter((l) => l.status === adminFilter);

  // ACTION column is only shown when there is at least one row the current
  // user is permitted to act on (not own leave, and admin cannot act on
  // admin/superadmin leaves).
  const hasActionableRows = filteredAll.some((l) => {
    if (l.uid === userData?.uid) return false;
    if (userData?.role === "admin") {
      const role = l.applicantRole || "employee";
      if (role === "admin" || role === "superadmin") return false;
    }
    return l.status === "pending";
  });

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading)
    return <div className={styles.loadingState}>Loading leave data…</div>;

  return (
    <div className={styles.leaveContainer}>
      {/* ── Header ── */}
      <div className={styles.leaveHeader}>
        <div className={styles.leaveHeaderLeft}>
          <FaCalendarAlt className={styles.leaveHeaderIcon} />
          <div>
            <h2 className={styles.leaveHeaderTitle}>
              {viewUser?.name || viewUser?.displayName || (isViewingOther ? "Employee" : "My")} Leaves
            </h2>
            <p className={styles.leaveHeaderSub}>
              {currentYear} · {viewUser?.department || viewUser?.role || "Employee"}
            </p>
          </div>
        </div>
        {canManage && pendingCount > 0 && (
          <span className={styles.pendingBadge}>
            {pendingCount} pending approval{pendingCount !== 1 ? "s" : ""}
          </span>
        )}
        {userData?.role === "superadmin" && (
          <button
            onClick={() => setShowManageModal(true)}
            style={{ padding: "7px 14px", fontSize: "0.78rem", fontWeight: 700, border: "1px solid #3b82f6", borderRadius: 7, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}
          >
            <FaEdit /> Manage Balances
          </button>
        )}
      </div>

      {/* ── Probation Banner ── */}
      {probationInfo.inProbation && (
        <div className={styles.probationBanner}>
          <div className={styles.probationBannerInner}>
            <FaLock className={styles.probationBannerIcon} />
            <div className={styles.probationBannerText}>
              <strong>🔒 Probation Period Active</strong>
              <span>
                {viewUser?.name || "This employee"} is currently in the <strong>3-month Probation Period</strong>
                {probationInfo.extended ? " (extended by 3 months)" : ""}.
                Leave applications are <strong>not permitted</strong> during probation{probationInfo.extended ? " (extended period)" : ""}, except in genuine emergencies with prior HR approval.
                Probation ends on <strong>{probationInfo.endsOn}</strong> — {probationInfo.daysLeft} day{probationInfo.daysLeft !== 1 ? "s" : ""} remaining.
              </span>
            </div>
            {userData?.role === "superadmin" && (
              <div className={styles.probationBannerActions}>
                {!probationInfo.extended ? (
                  <button
                    className={styles.probationExtendBtn}
                    onClick={handleExtendProbation}
                    disabled={extendingProbation}
                    title="Extend probation by another 3 months (total 6 months from joining)"
                  >
                    <FaUserClock /> {extendingProbation ? "Extending…" : "Extend Probation (+3 mo)"}
                  </button>
                ) : (
                  <button
                    className={styles.probationRevokeBtn}
                    onClick={handleRevokeProbationExtension}
                    disabled={extendingProbation}
                    title="Revert to standard 3-month probation"
                  >
                    <FaTimes /> {extendingProbation ? "Reverting…" : "Revoke Extension"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* If NOT in probation but superadmin viewing employee — show extension control in a subtle bar */}
      {!probationInfo.inProbation && userData?.role === "superadmin" && isViewingOther && viewUser?.joiningDate && (
        <div className={styles.probationControlBar}>
          <FaUserClock className={styles.probationControlIcon} />
          <span>
            Probation completed{probationInfo.extended ? " (was extended)" : ""}.
            Joined: <strong>{probationInfo.joinDate}</strong>.
          </span>
          {probationInfo.extended && (
            <button
              className={styles.probationRevokeBtn}
              onClick={handleRevokeProbationExtension}
              disabled={extendingProbation}
              style={{ marginLeft: 8 }}
            >
              <FaTimes /> Revoke Extension Record
            </button>
          )}
          {!probationInfo.extended && (
            <button
              className={styles.probationExtendBtn}
              onClick={handleExtendProbation}
              disabled={extendingProbation}
              style={{ marginLeft: 8 }}
            >
              <FaUserClock /> Re-trigger Extended Probation
            </button>
          )}
        </div>
      )}

      {/* ── Balance Cards ── */}
      <div className={styles.balanceGrid}>
        {LEAVE_KEYS.map((key) => {
          const type = LEAVE_TYPES[key];
          const bal = myBalance?.[key] ?? { total: type.defaultDays, taken: 0, pending: 0 };

          // ── Unpaid Leave card (no cap) ──────────────────────────────────
          if (key === "UL") {
            const ulDeduction = myLeaves
              .filter((l) => l.type === "UL" && l.status === "approved")
              .reduce((s, l) => s + (l.deductionAmount || 0), 0);
            return (
              <div key={key} className={styles.balanceCard} style={{ "--card-color": type.color }}>
                <div className={styles.balanceCardHeader}>
                  <span className={styles.balanceCardShort} style={{ color: type.color }}>{type.short}</span>
                  <span className={styles.balanceCardTotal} style={{ color: "#ef4444", fontWeight: 700 }}>No Limit</span>
                </div>
                <div className={styles.balanceCardLabel}>{type.label}</div>
                <div className={styles.balanceProgressBar}>
                  <div style={{ height: "100%", width: bal.taken > 0 ? "100%" : "0%", background: "#ef4444", borderRadius: 999 }} />
                </div>
                <div className={styles.balanceCardStats}>
                  <div className={styles.balanceStat}>
                    <span className={styles.balanceStatNum} style={{ color: "#ef4444" }}>{bal.taken}</span>
                    <span className={styles.balanceStatLabel}>Taken</span>
                  </div>
                  {bal.pending > 0 && (
                    <div className={styles.balanceStat}>
                      <span className={styles.balanceStatNum} style={{ color: "#f59e0b" }}>{bal.pending}</span>
                      <span className={styles.balanceStatLabel}>Pending</span>
                    </div>
                  )}
                  {ulDeduction > 0 && (
                    <div className={styles.balanceStat}>
                      <span className={styles.balanceStatNum} style={{ color: "#ef4444", fontSize: "0.78rem" }}>
                        ₹{ulDeduction.toLocaleString("en-IN")}
                      </span>
                      <span className={styles.balanceStatLabel}>Deducted</span>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // ── Regular paid leave card ─────────────────────────────────────
          const available = bal.total - bal.taken - bal.pending;
          const usedPct = Math.min(
            100,
            Math.round(((bal.taken + bal.pending) / bal.total) * 100)
          );
          return (
            <div
              key={key}
              className={styles.balanceCard}
              style={{ "--card-color": type.color }}
            >
              <div className={styles.balanceCardHeader}>
                <span
                  className={styles.balanceCardShort}
                  style={{ color: type.color }}
                >
                  {type.short}
                </span>
                <span className={styles.balanceCardTotal}>
                  {bal.total} days
                  {bal.carriedForward > 0 && (
                    <span style={{ fontSize: "0.6rem", marginLeft: 5, background: "#d1fae5", color: "#065f46", padding: "1px 5px", borderRadius: 4, fontWeight: 700, verticalAlign: "middle" }}>
                      +{bal.carriedForward} carried
                    </span>
                  )}
                </span>
              </div>
              <div className={styles.balanceCardLabel}>{type.label}</div>
              <div className={styles.balanceProgressBar}>
                <div
                  className={styles.balanceProgressFill}
                  style={{ width: `${usedPct}%`, background: type.color }}
                />
              </div>
              <div className={styles.balanceCardStats}>
                <div className={styles.balanceStat}>
                  <span
                    className={styles.balanceStatNum}
                    style={{ color: type.color }}
                  >
                    {available}
                  </span>
                  <span className={styles.balanceStatLabel}>Available</span>
                </div>
                <div className={styles.balanceStat}>
                  <span className={styles.balanceStatNum}>{bal.taken}</span>
                  <span className={styles.balanceStatLabel}>Taken</span>
                </div>
                {bal.pending > 0 && (
                  <div className={styles.balanceStat}>
                    <span
                      className={styles.balanceStatNum}
                      style={{ color: "#f59e0b" }}
                    >
                      {bal.pending}
                    </span>
                    <span className={styles.balanceStatLabel}>Pending</span>
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabRow}>
        <button
          className={`${styles.tab} ${activeTab === "balance" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("balance")}
        >
          <FaCalendarAlt /> Balance
        </button>
        {!isViewingOther && (
          <button
            className={`${styles.tab} ${activeTab === "apply" ? styles.tabActive : ""} ${probationInfo.inProbation ? styles.tabDisabled : ""}`}
            onClick={() => setActiveTab("apply")}
            title={probationInfo.inProbation ? `Leave applications are locked during probation. Probation ends ${probationInfo.endsOn}.` : undefined}
          >
            {probationInfo.inProbation ? <FaLock /> : <FaPlus />} Apply
            {probationInfo.inProbation && <span className={styles.tabLockBadge}>Locked</span>}
          </button>
        )}
        <button
          className={`${styles.tab} ${activeTab === "history" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("history")}
        >
          <FaHistory /> History
        </button>
        {canManage && (
          <button
            className={`${styles.tab} ${activeTab === "all-requests" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("all-requests")}
          >
            <FaUsers /> All Requests
            {pendingCount > 0 && (
              <span className={styles.tabBadge}>{pendingCount}</span>
            )}
          </button>
        )}
        <button
          className={`${styles.tab} ${activeTab === "policy" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("policy")}
        >
          <FaShieldAlt /> Leave Policy &amp; T&amp;C
        </button>
      </div>

      {/* ── Balance Tab ── */}
      {activeTab === "balance" && (
        <div className={styles.leaveTableWrap}>
          <div className={styles.tableHeader}>
            <h4 className={styles.tableTitle}>
              Leave Balances · {currentYear}
            </h4>
          </div>

          {/* ── Detailed table ── */}
          <table className={styles.leaveTable}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Total</th>
                <th>Taken</th>
                <th>Pending</th>
                <th>Available</th>
                <th>Next Year?</th>
                <th>On Exit</th>
                <th>Salary Deducted</th>
                <th>Policy</th>
              </tr>
            </thead>
            <tbody>
              {LEAVE_KEYS.map((key) => {
                const type = LEAVE_TYPES[key];
                const bal = myBalance?.[key] ?? { total: type.defaultDays, taken: 0, pending: 0 };
                const isUL = key === "UL";
                const available = isUL ? null : bal.total - bal.taken - bal.pending;
                const ulDed = isUL
                  ? myLeaves
                      .filter((l) => l.type === "UL" && l.status === "approved")
                      .reduce((s, l) => s + (l.deductionAmount || 0), 0)
                  : 0;
                return (
                  <tr key={key}>
                    <td><LeaveTypePill type={key} /></td>
                    <td>
                      {isUL
                        ? <span style={{ color: "#ef4444", fontWeight: 700 }}>No Limit</span>
                        : (
                          <>
                            {bal.total}
                            {bal.carriedForward > 0 && (
                              <span style={{ display: "block", fontSize: "0.65rem", color: "#16a34a", fontWeight: 700 }}>
                                (+{bal.carriedForward} carried fwd)
                              </span>
                            )}
                          </>
                        )}
                    </td>
                    <td>{bal.taken}</td>
                    <td style={{ color: bal.pending > 0 ? "#f59e0b" : "inherit" }}>{bal.pending}</td>
                    <td>
                      {isUL
                        ? <span style={{ color: "#ef4444", fontWeight: 700 }}>No Limit</span>
                        : <strong style={{ color: type.color }}>{available}</strong>}
                    </td>
                    <td style={{ fontSize: "0.72rem" }}>
                      {type.carryForward === "yes" && <span style={{ color: "#16a34a", fontWeight: 700 }}>Yes (max {type.carryMax}d)</span>}
                      {type.carryForward === "no" && <span style={{ color: "#dc2626", fontWeight: 600 }}>No — lapses</span>}
                      {type.carryForward === "conditional" && <span style={{ color: "#d97706", fontWeight: 600 }}>State-dependent</span>}
                      {type.carryForward === "na" && <span style={{ color: "#9ca3af" }}>N/A</span>}
                    </td>
                    <td style={{ fontSize: "0.72rem" }}>
                      {type.onSeparation === "encashed" && <span style={{ color: "#2563eb", fontWeight: 700 }}>Encashed ✓</span>}
                      {type.onSeparation === "lapses" && <span style={{ color: "#dc2626", fontWeight: 600 }}>Lapses</span>}
                      {type.onSeparation === "na" && <span style={{ color: "#9ca3af" }}>N/A</span>}
                    </td>
                    <td>
                      {isUL && ulDed > 0
                        ? <strong style={{ color: "#ef4444" }}>₹{ulDed.toLocaleString("en-IN")}</strong>
                        : <span style={{ color: "#9ca3af" }}>—</span>}
                    </td>
                    <td style={{ fontSize: "0.72rem", color: "#64748b" }}>{type.desc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Compact summary table ── */}
          {(() => {
            const summaryKeys = LEAVE_KEYS.filter((k) => LEAVE_TYPES[k].defaultDays !== null && k !== "ML");
            return (
              <div style={{ overflowX: "auto", marginTop: 18 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.8rem", border: "1.5px solid #cbd5e1" }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #cbd5e1", padding: "6px 12px", textAlign: "left", background: "#f1f5f9", fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                        Leaves
                      </th>
                      {summaryKeys.map((k) => (
                        <th key={k} style={{ border: "1px solid #cbd5e1", padding: "6px 14px", textAlign: "center", background: "#f1f5f9", fontWeight: 700, color: LEAVE_TYPES[k].color, whiteSpace: "nowrap" }}>
                          {LEAVE_TYPES[k].short}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #cbd5e1", padding: "5px 12px", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>Leave C/F &amp; Assigned</td>
                      {summaryKeys.map((k) => {
                        const bal = myBalance?.[k];
                        const total = bal?.total ?? LEAVE_TYPES[k].defaultDays;
                        return (
                          <td key={k} style={{ border: "1px solid #cbd5e1", padding: "5px 14px", textAlign: "center", color: "#1e293b" }}>{total}</td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #cbd5e1", padding: "5px 12px", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>Leave Availed</td>
                      {summaryKeys.map((k) => {
                        const bal = myBalance?.[k];
                        const taken = bal?.taken ?? 0;
                        return (
                          <td key={k} style={{ border: "1px solid #cbd5e1", padding: "5px 14px", textAlign: "center", color: "#374151" }}>{taken}</td>
                        );
                      })}
                    </tr>
                    <tr style={{ background: "#f8fafc" }}>
                      <td style={{ border: "1px solid #cbd5e1", padding: "5px 12px", fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap" }}>Leave Balance</td>
                      {summaryKeys.map((k) => {
                        const bal = myBalance?.[k];
                        const total = bal?.total ?? LEAVE_TYPES[k].defaultDays;
                        const taken = bal?.taken ?? 0;
                        const pending = bal?.pending ?? 0;
                        const remaining = total - taken - pending;
                        const color = remaining <= 0 ? "#dc2626" : remaining <= 2 ? "#d97706" : "#16a34a";
                        return (
                          <td key={k} style={{ border: "1px solid #cbd5e1", padding: "5px 14px", textAlign: "center", fontWeight: 800, color }}>{remaining}</td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Apply Tab ── */}
      {activeTab === "apply" && (
        <div>
          {/* ── Probation block screen ── */}
          {probationInfo.inProbation && (
            <div className={styles.probationBlock}>
              <div className={styles.probationBlockIcon}>
                <FaLock />
              </div>
              <h3 className={styles.probationBlockTitle}>Leave Applications Locked — Probation Period</h3>
              <p className={styles.probationBlockBody}>
                You are currently in your <strong>3-month Probation Period</strong>
                {probationInfo.extended ? " (extended by an additional 3 months)" : ""}.
                As per Company Policy (Section 2.6 &amp; Section 4.0 of the HR Manual), <strong>no leave of any kind
                may be applied for or availed during the Probation Period</strong>.
              </p>
              <div className={styles.probationBlockDetails}>
                <div className={styles.probationBlockRow}>
                  <FaCalendarAlt className={styles.probationBlockRowIcon} />
                  <span>Joined on: <strong>{probationInfo.joinDate}</strong></span>
                </div>
                <div className={styles.probationBlockRow}>
                  <FaClock className={styles.probationBlockRowIcon} />
                  <span>Probation ends on: <strong>{probationInfo.endsOn}</strong></span>
                </div>
                <div className={styles.probationBlockRow}>
                  <FaUserClock className={styles.probationBlockRowIcon} />
                  <span>Days remaining: <strong>{probationInfo.daysLeft} day{probationInfo.daysLeft !== 1 ? "s" : ""}</strong></span>
                </div>
              </div>
              <div className={styles.probationBlockRules}>
                <p className={styles.probationBlockRulesTitle}><FaExclamationTriangle /> Policy Rules During Probation</p>
                <ul className={styles.probationBlockRulesList}>
                  <li>No Earned Leave (EL), Casual Leave (CL), Sick Leave (SL), or any other leave type can be applied for or availed.</li>
                  <li>Leave entitlements begin to accrue <strong>only after probation ends</strong> — there is no retroactive accrual for the probation months.</li>
                  <li>The only exception is a <strong>genuine emergency</strong>, which must be communicated to HR immediately for a discretionary decision. Even then, it will be recorded as Leave Without Pay (LWP).</li>
                  <li>Repeated or unexplained absences during probation may lead to probation extension or termination.</li>
                  <li>This lock is automatic and is based on your Date of Joining recorded in the system.</li>
                </ul>
              </div>
              <p className={styles.probationBlockFooter}>
                For genuine emergencies, contact HR via <strong>Chat with Admin</strong> directly.
              </p>
            </div>
          )}

          {/* ── Actual apply form — only rendered when NOT in probation ── */}
          {!probationInfo.inProbation && (
        <form className={styles.applyForm} onSubmit={handleSubmit}>
          <h4 className={styles.formTitle}>New Leave Request</h4>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Leave Type</label>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value }))
                }
              >
                {LEAVE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {LEAVE_TYPES[k].label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>From</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    startDate: e.target.value,
                    endDate:
                      e.target.value > f.endDate ? e.target.value : f.endDate,
                  }))
                }
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>To</label>
              <input
                type="date"
                value={form.endDate}
                min={form.startDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, endDate: e.target.value }))
                }
                required
              />
            </div>
            <div className={styles.formDayCount}>
              {formDays} day{formDays !== 1 ? "s" : ""}
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.checkboxRow}>
              <input
                type="checkbox"
                id="halfDay"
                checked={form.halfDay}
                onChange={(e) =>
                  setForm((f) => ({ ...f, halfDay: e.target.checked }))
                }
              />
              <label htmlFor="halfDay">Half Day</label>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Reason</label>
            <textarea
              rows={3}
              value={form.reason}
              placeholder="Describe the reason for leave…"
              onChange={(e) =>
                setForm((f) => ({ ...f, reason: e.target.value }))
              }
              required
            />
          </div>
          {/* UL: proof type + proof note */}
          {form.type === "UL" && (
            <>
              <div className={styles.formGroup}>
                <label>Reason Category <span style={{ color: "#ef4444" }}>*</span></label>
                <select
                  value={form.proofType}
                  onChange={(e) => setForm((f) => ({ ...f, proofType: e.target.value }))}
                  required
                >
                  <option value="">— Select category —</option>
                  <option value="medical">Medical / Health Condition</option>
                  <option value="family_medical">Family Medical Emergency</option>
                  <option value="personal_emergency">Personal Emergency (Accident / Natural Disaster)</option>
                  <option value="bereavement_extended">Bereavement — Extended (beyond BL entitlement)</option>
                  <option value="personal">Personal / Compelling Personal Reasons</option>
                  <option value="other">Other (as directed by HR)</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Proof / Supporting Document (to be submitted within 3 working days of return)</label>
                <textarea
                  rows={2}
                  value={form.proofNote}
                  placeholder="E.g. 'Medical certificate from Dr. Sharma, hospitalization records from XYZ Hospital'…"
                  onChange={(e) => setForm((f) => ({ ...f, proofNote: e.target.value }))}
                />
              </div>
            </>
          )}
          {/* UL salary deduction notice */}
          {form.type === "UL" && formDays > 0 && (
            <p className={styles.warnMsg} style={{ borderColor: "#fca5a5", background: "#fff5f5", color: "#991b1b" }}>
              ⚠ <strong>Unpaid Leave:</strong> {formDays} day{formDays !== 1 ? "s" : ""} will result in a salary
              deduction. Estimated: <em>Basic Salary ÷ 26 × {formDays}</em>. Exact amount confirmed on approval.
            </p>
          )}
          {/* Balance warning */}
          {(() => {
            const bal = myBalance?.[form.type];
            const remaining = bal
              ? bal.total - bal.taken - bal.pending
              : LEAVE_TYPES[form.type].defaultDays;
            if (
              formDays > remaining &&
              !BALANCE_EXEMPT.includes(form.type)
            ) {
              return (
                <p className={styles.warnMsg}>
                  ⚠ Only {remaining} day{remaining !== 1 ? "s" : ""} available
                  for {LEAVE_TYPES[form.type].short}. This request may be
                  rejected.
                </p>
              );
            }
            return null;
          })()}
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={submitting || probationInfo.inProbation}
          >
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
        </form>
          )}
        </div>
      )}

      {/* ── My History Tab ── */}
      {activeTab === "history" && (
        <div className={styles.leaveTableWrap}>
          <div className={styles.tableHeader}>
            <h4 className={styles.tableTitle}>
              {isViewingOther ? `${viewUser?.name || "Employee"}'s` : "My"} Leave History · {currentYear}
            </h4>
          </div>
          {myLeaves.length === 0 ? (
            <p className={styles.emptyMsg}>No leave requests found.</p>
          ) : (
            <table className={styles.leaveTable}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Deduction</th>
                  <th>Reason</th>
                  <th>Applied</th>
                  <th>Status</th>
                  <th>Reviewed By</th>
                </tr>
              </thead>
              <tbody>
                {myLeaves.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <LeaveTypePill type={l.type} />
                    </td>
                    <td>{l.startDate}</td>
                    <td>{l.endDate}</td>
                    <td>{l.days}</td>
                    <td>
                      {l.type === "UL" && l.status === "approved" && l.deductionAmount
                        ? <strong style={{ color: "#ef4444" }}>₹{l.deductionAmount.toLocaleString("en-IN")}</strong>
                        : l.type === "UL" && l.status === "pending"
                        ? <span style={{ color: "#9ca3af", fontSize: "0.72rem" }}>TBD on approval</span>
                        : <span style={{ color: "#9ca3af" }}>—</span>}
                    </td>
                    <td className={styles.reasonCell}>{l.reason}</td>
                    <td>
                      {l.appliedAt?.toDate
                        ? l.appliedAt.toDate().toLocaleDateString("en-IN", { timeZone: 'Asia/Kolkata' })
                        : "—"}
                    </td>
                    <td>
                      <StatusBadge status={l.status} />
                    </td>
                    <td>{l.reviewedBy || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── All Requests Tab (admin) ── */}
      {activeTab === "all-requests" && canManage && (
        <div className={styles.leaveTableWrap}>
          <div className={styles.tableHeader}>
            <h4 className={styles.tableTitle}>All Leave Requests</h4>
            <div className={styles.filterRow}>
              {["all", "pending", "approved", "rejected"].map((f) => (
                <button
                  key={f}
                  className={`${styles.filterBtn} ${adminFilter === f ? styles.filterBtnActive : ""}`}
                  onClick={() => setAdminFilter(f)}
                  style={{ textTransform: "capitalize" }}
                >
                  {f}
                  {f === "pending" && pendingCount > 0 && ` (${pendingCount})`}
                </button>
              ))}
            </div>
          </div>
          {filteredAll.length === 0 ? (
            <p className={styles.emptyMsg}>No {adminFilter} requests found.</p>
          ) : (
            <table className={styles.leaveTable}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Days</th>
                  <th>Deduction</th>
                  <th>Reason</th>
                  <th>Applied</th>
                  <th>Status</th>
                  {hasActionableRows && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {filteredAll.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontWeight: 600 }}>
                      {l.userName || l.uid?.slice(0, 8)}
                    </td>
                    <td>
                      <LeaveTypePill type={l.type} />
                    </td>
                    <td>{l.startDate}</td>
                    <td>{l.endDate}</td>
                    <td>{l.days}</td>
                    <td>
                      {l.type === "UL" && l.status === "approved" && l.deductionAmount
                        ? <strong style={{ color: "#ef4444" }}>₹{l.deductionAmount.toLocaleString("en-IN")}</strong>
                        : l.type === "UL"
                        ? <span style={{ color: "#9ca3af", fontSize: "0.72rem" }}>TBD on approval</span>
                        : <span style={{ color: "#9ca3af" }}>—</span>}
                    </td>
                    <td className={styles.reasonCell}>{l.reason}</td>
                    <td>
                      {l.appliedAt?.toDate
                        ? l.appliedAt.toDate().toLocaleDateString("en-IN", { timeZone: 'Asia/Kolkata' })
                        : "—"}
                    </td>
                    <td>
                      <StatusBadge status={l.status} />
                    </td>
                    {hasActionableRows && (
                      <td>
                        {l.status === "pending" ? (
                          (() => {
                            const applicantRole = l.applicantRole || "employee";
                            const isOwnLeave = l.uid === userData?.uid;
                            if (isOwnLeave) return null;

                            const isAdminReviewingAdmin =
                              userData?.role === "admin" &&
                              (applicantRole === "admin" || applicantRole === "superadmin");
                            if (isAdminReviewingAdmin) {
                              return (
                                <span style={{ fontSize: "0.72rem", color: "#b45309", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "3px 9px", fontWeight: 700, whiteSpace: "nowrap" }}>
                                  Superadmin only
                                </span>
                              );
                            }

                            return (
                              <div className={styles.actionBtns}>
                                <button
                                  className={styles.approveBtn}
                                  onClick={() => confirmReview(l, "approved")}
                                >
                                  <FaCheck /> Approve
                                </button>
                                <button
                                  className={styles.rejectBtn}
                                  onClick={() => confirmReview(l, "rejected")}
                                >
                                  <FaTimes /> Reject
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: "0.76rem" }}>
                            {l.reviewedBy || "—"}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Manage Balances Modal ── */}
      {showManageModal && (userData?.role === "superadmin" || userData?.manageLeaveAccess) && (() => {
        const bal = allBalances.find((b) => b.uid === viewUid);
        const isEditing = editingBalance?.id === bal?.id;
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) { setShowManageModal(false); setEditingBalance(null); } }}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          >
            <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 860, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)", display: "flex", flexDirection: "column" }}>
              {/* Modal header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "18px 22px 14px", borderBottom: "1px solid #e2e8f0", position: "sticky", top: 0, background: "#fff", borderRadius: "14px 14px 0 0", zIndex: 1 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FaEdit style={{ color: "#3b82f6" }} />
                    <span style={{ fontWeight: 800, fontSize: "1rem", color: "#1e293b" }}>Manage Leave Balances</span>
                  </div>
                  <p style={{ fontSize: "0.72rem", color: "#64748b", margin: "4px 0 0" }}>
                    {viewUser?.name || viewUser?.displayName || "Employee"} · {currentYear} 
                  </p>
                </div>
                <button
                  onClick={() => { setShowManageModal(false); setEditingBalance(null); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "1.2rem", lineHeight: 1, padding: 4 }}
                >
                  <FaTimes />
                </button>
              </div>

              {/* Modal body */}
              <div style={{ padding: "18px 22px 22px" }}>
                {!bal ? (
                  <p style={{ color: "#64748b", fontSize: "0.82rem" }}>No balance record found — will be created automatically when employee opens Leave page.</p>
                ) : (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                      {LEAVE_KEYS.filter((k) => LEAVE_TYPES[k].defaultDays !== null).map((k) => {
                        const t = LEAVE_TYPES[k];
                        const current = bal[k]?.total ?? t.defaultDays;
                        const rangeLabel = t.maxDays !== t.defaultDays ? `${t.defaultDays}–${t.maxDays}` : `${t.defaultDays}`;
                        return (
                          <div key={k} style={{ background: t.color + "08", border: `2px solid ${t.color}30`, borderRadius: 10, padding: "12px 16px", flex: "1 1 100px", minWidth: 100 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                              <span style={{ fontWeight: 800, fontSize: "0.95rem", color: t.color }}>{t.short}</span>
                              <span style={{ fontSize: "0.6rem", background: t.color + "20", color: t.color, padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>{rangeLabel}</span>
                            </div>
                            <div style={{ fontSize: "0.68rem", color: "#64748b", marginBottom: 8 }}>{t.label}</div>
                            {isEditing ? (
                              <input
                                type="number"
                                min={t.defaultDays}
                                max={t.maxDays ?? undefined}
                                value={editingBalance[`edit_${k}`]}
                                onChange={(e) => setEditingBalance((prev) => ({ ...prev, [`edit_${k}`]: e.target.value }))}
                                style={{ width: "100%", padding: "5px 8px", border: `1.5px solid ${t.color}`, borderRadius: 6, fontSize: "1rem", fontWeight: 700, textAlign: "center", fontFamily: "inherit", color: t.color, background: "#fff", boxSizing: "border-box" }}
                              />
                            ) : (
                              <div style={{ fontSize: "1.5rem", fontWeight: 800, color: t.color, textAlign: "center" }}>{current}</div>
                            )}
                            <div style={{ fontSize: "0.6rem", color: "#94a3b8", textAlign: "center", marginTop: 2 }}>days / year</div>
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
                      {isEditing ? (
                        <>
                          <button className={styles.rejectBtn} onClick={() => setEditingBalance(null)}><FaTimes /> Cancel</button>
                          <button className={styles.approveBtn} onClick={async () => { await handleUpdateTotal(); setShowManageModal(false); }} disabled={savingBalance}>
                            <FaCheck /> {savingBalance ? "Saving…" : "Save Changes"}
                          </button>
                        </>
                      ) : (
                        <button
                          style={{ padding: "8px 20px", fontSize: "0.82rem", fontWeight: 700, border: "1px solid #3b82f6", borderRadius: 7, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}
                          onClick={() => {
                            const init = { id: bal.id, uid: bal.uid };
                            LEAVE_KEYS.filter((k) => LEAVE_TYPES[k].defaultDays !== null).forEach((k) => {
                              init[`edit_${k}`] = bal[k]?.total ?? LEAVE_TYPES[k].defaultDays;
                            });
                            setEditingBalance(init);
                          }}
                        >
                          <FaEdit /> Edit Balances
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* Access Control — superadmin only */}
                {userData?.role === "superadmin" && (
                  <div style={{ marginTop: 24, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <FaUsers style={{ color: "#3b82f6", flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1e293b" }}>Access Control</span>
                      <span style={{ fontSize: "0.68rem", color: "#64748b" }}>Grant / revoke Manage Balances access</span>
                    </div>

                    {/* Filter bar */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <input
                        type="text"
                        placeholder="Search by name, role or dept…"
                        value={accessSearch}
                        onChange={(e) => setAccessSearch(e.target.value)}
                        style={{ flex: "1 1 180px", padding: "6px 10px", fontSize: "0.78rem", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "inherit", outline: "none", minWidth: 0 }}
                      />
                      {["all", "granted", "none"].map((opt) => (
                        <button
                          key={opt}
                          onClick={() => setAccessStatusFilter(opt)}
                          style={{ padding: "6px 13px", fontSize: "0.72rem", fontWeight: 700, borderRadius: 6, fontFamily: "inherit", cursor: "pointer", border: accessStatusFilter === opt ? "1.5px solid #3b82f6" : "1px solid #cbd5e1", background: accessStatusFilter === opt ? "#eff6ff" : "#f8fafc", color: accessStatusFilter === opt ? "#1d4ed8" : "#64748b", transition: "all 0.15s" }}
                        >
                          {opt === "all" ? "All" : opt === "granted" ? "Granted" : "No Access"}
                        </button>
                      ))}
                    </div>

                    <div style={{ maxHeight: 260, overflowY: "auto", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <table className={styles.leaveTable} style={{ marginBottom: 0 }}>
                      <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "#f8fafc" }}>
                        <tr>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Access</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allUsers
                          .filter((u) => u.role !== "superadmin")
                          .filter((u) => {
                            const q = accessSearch.trim().toLowerCase();
                            const matchText = !q || (u.name || "").toLowerCase().includes(q) || (u.role || "").toLowerCase().includes(q) || (u.department || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                            const matchStatus = accessStatusFilter === "all" || (accessStatusFilter === "granted" ? !!u.manageLeaveAccess : !u.manageLeaveAccess);
                            return matchText && matchStatus;
                          })
                          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                          .map((u) => (
                            <tr key={u.id}>
                              <td style={{ fontWeight: 600 }}>{u.name || u.email}</td>
                              <td style={{ textTransform: "capitalize", color: "#64748b" }}>{u.role}</td>
                              <td>
                                {u.manageLeaveAccess
                                  ? <span style={{ background: "#d1fae5", color: "#065f46", padding: "2px 8px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700 }}>Granted</span>
                                  : <span style={{ background: "#f1f5f9", color: "#94a3b8", padding: "2px 8px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 600 }}>None</span>
                                }
                              </td>
                              <td>
                                <button
                                  disabled={togglingAccess === u.id}
                                  onClick={() => handleToggleAccess(u.id, u.manageLeaveAccess)}
                                  style={{ padding: "3px 10px", fontSize: "0.72rem", fontWeight: 700, border: u.manageLeaveAccess ? "1px solid #dc2626" : "1px solid #16a34a", borderRadius: 5, background: u.manageLeaveAccess ? "#fee2e2" : "#d1fae5", color: u.manageLeaveAccess ? "#dc2626" : "#065f46", cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4 }}
                                >
                                  {togglingAccess === u.id ? "…" : u.manageLeaveAccess ? <><FaTimes /> Revoke</> : <><FaCheck /> Grant</>}
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Leave Policy & T&C Tab ── */}
      {activeTab === "policy" && (
        <div className={styles.policyWrap}>
          <div className={styles.policyHeader}>
            <FaShieldAlt className={styles.policyHeaderIcon} />
            <div>
              <h3 className={styles.policyTitle}>Leave Policy — Terms &amp; Conditions</h3>
              <p className={styles.policySub}>
                All leave entitlements comply with applicable central and state labour law in force from time to time.
                Statutory minimums are guaranteed. Where this policy is more generous than statute, the policy applies.
                Where statute mandates more, statute prevails. Subject to Company approval and revision with lawful notice.
              </p>
            </div>
          </div>

          {/* ── Carry-Forward Quick Reference Table ── */}
          <div className={styles.carryTable}>
            <div className={styles.carryTableTitle}>Leave Carry-Forward &amp; Encashment — Quick Reference</div>
            <div className={styles.proofTableWrap} style={{ margin: 0 }}>
              <table className={styles.proofTable}>
                <thead>
                  <tr>
                    <th>Leave Type</th>
                    <th>Carries Forward?</th>
                    <th>Max Accumulation</th>
                    <th>Encashable During Service?</th>
                    <th>Encashable on Separation?</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>EL / PL</strong></td>
                    <td><span style={{ color: "#16a34a", fontWeight: 700 }}>✓ Yes</span></td>
                    <td>Up to <strong>30 days</strong> max (balance beyond 30 lapses at year-end)</td>
                    <td>No (not permitted during service)</td>
                    <td><span style={{ color: "#16a34a", fontWeight: 700 }}>✓ Mandatory</span> — all unused EL paid out at basic ÷ 26 per day</td>
                  </tr>
                  <tr>
                    <td><strong>CL</strong></td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span> — lapses at year-end</td>
                    <td>N/A (use-it-or-lose-it)</td>
                    <td>No</td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span> — lapses on separation</td>
                  </tr>
                  <tr>
                    <td><strong>SL</strong></td>
                    <td><span style={{ color: "#d97706", fontWeight: 700 }}>Conditional</span> — carries forward in states where applicable law requires; otherwise lapses</td>
                    <td>Up to <strong>45 days</strong> accumulation where permitted by applicable state law</td>
                    <td>No</td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span> — not encashable on separation under most applicable law</td>
                  </tr>
                  <tr>
                    <td><strong>ML</strong></td>
                    <td>N/A — per-event statutory right</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>N/A</td>
                  </tr>
                  <tr>
                    <td><strong>PL (Paternity)</strong></td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span> — per-event, must be taken at the time</td>
                    <td>N/A</td>
                    <td>No</td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span></td>
                  </tr>
                  <tr>
                    <td><strong>BL</strong></td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span> — per-event</td>
                    <td>N/A</td>
                    <td>No</td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span></td>
                  </tr>
                  <tr>
                    <td><strong>UL</strong></td>
                    <td>N/A — discretionary, no balance</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td><span style={{ color: "#dc2626", fontWeight: 700 }}>✗ No</span> — deducted salary already settled in payroll</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 0: live probation status banner inside policy tab ── */}
          {probationInfo.inProbation && (
            <div className={styles.policyAlert} style={{ borderColor: "#dc2626", background: "#fef2f2", color: "#7f1d1d", borderLeft: "4px solid #dc2626", marginBottom: 16 }}>
              <strong>🔒 YOU ARE CURRENTLY IN PROBATION</strong> — Leave applications are <strong>disabled</strong> until <strong>{probationInfo.endsOn}</strong> ({probationInfo.daysLeft} days remaining).
              {probationInfo.extended && " | Probation has been extended by Superadmin (+3 months)."}
              &nbsp;Refer to <strong>Section 2.6</strong> below for the full policy.
            </div>
          )}

          <PolicySection title="2.6  Probation Period Leave Policy — 3-Month Restriction (HR Manual Reference)" highlight>
            <div className={styles.policyAlert} style={{ borderColor: "#dc2626", background: "#fef2f2", color: "#7f1d1d", borderLeft: "4px solid #dc2626" }}>
              ⚠ <strong>PROBATION RESTRICTION:</strong> No leave of any kind may be applied for or availed during the first <strong>3 (Three) calendar months</strong> from the Date of Joining (the Probation Period). This is a hard system restriction and is automatically enforced on the portal.
            </div>
            <ol className={styles.policyList}>
              <li>
                <strong>Duration of Probation Period:</strong> The first <strong>3 (Three) calendar months</strong> from an employee's Date of Joining constitute the mandatory Probation Period. During this entire period, the employee is under close assessment for performance, conduct, attendance, and adherence to Company policies.
              </li>
              <li>
                <strong>No Leave During Probation (Absolute Rule):</strong> No leave of any kind — including Earned Leave (EL), Casual Leave (CL), Sick Leave (SL), Maternity Leave (where not yet a statutory entitlement from Day 1), or any other paid or unpaid leave — may be applied for, sanctioned, or availed during the Probation Period.
              </li>
              <li>
                <strong>Sole Exception — Genuine Emergency:</strong> In the event of a genuine, unavoidable emergency, the employee must <em>immediately</em> inform the reporting manager or HR (via Chat with Admin), obtain verbal or written approval before the absence where possible, and regularise the absence upon return. Even in such cases, the absence shall be classified as <strong>Leave Without Pay (LWP)</strong> at the Company's discretion and shall not be credited to any leave balance.
              </li>
              <li>
                <strong>Leave Accrual:</strong> No leave of any type accrues during the Probation Period. All leave entitlements (EL, CL, SL, etc.) begin to accrue only from the first day after successful completion of probation. There is no retroactive credit for the probation months.
              </li>
              <li>
                <strong>No Carry-Forward of Probation Leaves:</strong> Any LWP or exceptional leave taken during probation does not carry forward and cannot be adjusted against any future leave balance.
              </li>
              <li>
                <strong>Consequence of Repeated Absence During Probation:</strong> Repeated or unexplained absences during the Probation Period may result in {""}(a) extension of the probation period by up to an additional 3 months, or {""}(b) non-confirmation and termination of employment at the end of probation.
              </li>
              <li>
                <strong>Portal Enforcement:</strong> The Employee Portal automatically disables the Leave Application tab during the probation period based on the employee's Date of Joining. The Submit button is technically blocked to prevent inadvertent applications. This is a system safeguard and does not replace the employee's personal responsibility to comply with this policy.
              </li>
              <li>
                <strong>Probation Extension — Superadmin Power:</strong> In rare cases where performance or conduct warrants further monitoring, the Superadmin may extend the probation period by an additional <strong>3 months</strong> (making the total probation period 6 months from joining). During this extended period, the same leave restriction applies — no leave may be availed except in genuine emergencies as described above. The extension is recorded in the system and the employee is notified.
              </li>
              <li>
                <strong>Confirmation:</strong> Probation ends only upon a formal written confirmation letter issued by the Company. <em>No confirmation shall be implied from silence or continued employment beyond the probation period.</em>
              </li>
              <li>
                <strong>Statutory Rights:</strong> This restriction does not override any right that is guaranteed from Day 1 under applicable central law (e.g., Maternity Benefit Act entitlements for qualifying employees). Where a statutory right exists from the first day of employment, it is honoured regardless of probation status. This policy applies only to the Company's discretionary leave types.
              </li>
            </ol>
          </PolicySection>

          <PolicySection title="1. General Provisions">
            <ol className={styles.policyList}>
              <li>All leave entitlements are governed by applicable central and state labour laws. The Company ensures compliance with minimum statutory entitlements at all times.</li>
              <li>Leave must be applied for in advance wherever possible. Emergency leave must be communicated at the earliest and regularised upon return to work.</li>
              <li>Misrepresentation of leave grounds, submission of false or fabricated supporting documents, or abuse of the leave system constitutes <strong>gross misconduct</strong> and may result in disciplinary action including termination of employment.</li>
              <li><strong>Leave during the Probation Period (first 3 months) is fully restricted.</strong> No leave of any kind may be availed during the Probation Period except in genuine emergencies with prior HR approval. See Section 2.6 above for full details.</li>
              <li>The Company reserves the right to defer leave (except statutory entitlements guaranteed by law) where operationally critical, with reasonable notice to the employee.</li>
              <li>Leave cannot be taken as a right in excess of the entitlements set out herein. Any absence not covered by approved leave shall be treated as Loss of Pay (LOP).</li>
              <li>Employees must ensure proper handover of duties before proceeding on any leave to ensure business continuity.</li>
            </ol>
          </PolicySection>

          <PolicySection title="2. Earned / Privileged Leave (EL / PL)">
            <ol className={styles.policyList}>
              <li><strong>Accrual:</strong> EL accrues at the rate of <strong>1.25 working days per completed calendar month</strong> (15 days per year). Accrual begins after successful completion of the probation period or as applicable under applicable law.</li>
              <li><strong>Carry-Forward:</strong> Unused EL <strong>carries forward</strong> to the following leave year. However, the maximum EL balance at any point shall be capped at <strong>30 days</strong>. Any EL in excess of the 30-day cap at year-end <strong>shall lapse</strong> and not be encashed. Employees are advised to utilise leave regularly to avoid lapse.</li>
              <li><strong>Encashment during service:</strong> EL encashment while in active employment is <strong>not permitted</strong>. Leave must be availed to be of benefit.</li>
              <li><strong>Encashment on separation:</strong> On resignation, termination (except for misconduct unless otherwise provided by applicable law), retirement, or death, all accumulated EL (up to the 30-day cap) shall be <strong>mandatorily encashed</strong> at the rate of: <em>Basic Salary ÷ 26 × number of unutilised EL days</em>. This is a statutory right and cannot be waived.</li>
              <li>A minimum of <strong>7 working days' advance notice</strong> is required for EL applications. Management may defer (not cancel) an approved EL plan if there are critical operational needs, with reasonable parallel notice to the employee.</li>
              <li>EL may not be combined with public holidays or weekly-offs to create prolonged absences exceeding <strong>10 consecutive days</strong> without prior written management approval.</li>
            </ol>
          </PolicySection>

          <PolicySection title="3. Casual Leave (CL)">
            <ol className={styles.policyList}>
              <li><strong>Carry-Forward:</strong> Casual Leave does <strong>NOT carry forward</strong> to the next leave year. All unused CL <strong>lapses at year-end (31 December / last day of the leave year)</strong> with zero encashment value.</li>
              <li><strong>On Leaving the Company:</strong> Unused CL <strong>lapses on the last working day</strong>. No payment or encashment is due for unutilised Casual Leave on resignation, termination, or retirement under any circumstances.</li>
              <li>Casual Leave is <strong>non-encashable</strong> at any point during or after service.</li>
              <li>CL shall not ordinarily exceed <strong>3 consecutive calendar days</strong>. Beyond this, absence shall be converted to Earned Leave or treated as Loss of Pay at management discretion.</li>
              <li>Application must be submitted at least <strong>1 working day in advance</strong> where possible.</li>
              <li>CL cannot be clubbed with other leave types without prior written approval.</li>
            </ol>
          </PolicySection>

          <PolicySection title="4. Sick Leave (SL)">
            <ol className={styles.policyList}>
              <li>A <strong>medical certificate from a registered medical practitioner</strong> is mandatory for sick leave exceeding <strong>2 consecutive working days</strong>.</li>
              <li><strong>Carry-Forward:</strong> SL carry-forward depends on the applicable state law at the place of employment. Where applicable law mandates carry-forward, unused SL carries forward up to a maximum accumulation of <strong>45 days</strong>; any excess lapses. Where applicable law does not mandate carry-forward, SL lapses at year-end. Employees should confirm their state entitlement with HR.</li>
              <li><strong>Not encashable on separation:</strong> SL balance is <strong>not encashable</strong> upon resignation, termination, or retirement (unless otherwise required by applicable law in the state of employment).</li>
              <li>The Company reserves the right to request a fitness-to-return certificate from a Company-nominated physician before resumption of duties after prolonged illness (&gt; 5 working days).</li>
              <li>Patterns of frequent, short-duration sick leave (e.g., recurring Monday/Friday absences) may be reviewed under the Company's attendance management framework.</li>
              <li>Fraudulent sick leave claims constitute misconduct under the Company's disciplinary policy.</li>
            </ol>
          </PolicySection>

          <PolicySection title="5. Maternity Leave (ML)">
            <ol className={styles.policyList}>
              <li>Female employees are entitled to maternity leave as per applicable statutory legislation. The Company shall fully comply with all requirements of applicable maternity benefit law.</li>
              <li>Employees must notify HR at least <strong>8 weeks prior to the expected delivery date</strong> and submit requisite medical documentation.</li>
              <li>Failure to return from maternity leave without prior written extension approval may be treated as abandonment of service in accordance with applicable law.</li>
              <li>The Company shall not require or permit an employee to work during the period of protected maternity leave.</li>
            </ol>
          </PolicySection>

          <PolicySection title="6. Paternity Leave (PL)">
            <ol className={styles.policyList}>
              <li><strong>Carry-Forward:</strong> Paternity Leave does <strong>NOT carry forward</strong>. It is a per-event entitlement tied to the specific birth or adoption event. Unused PL lapses at year-end.</li>
              <li><strong>On Leaving the Company:</strong> Paternity Leave <strong>lapses on separation</strong> — it is not encashable and has no monetary value at exit.</li>
              <li>Applicable to male employees on the birth or legal adoption of a child, subject to applicable law and Company policy.</li>
              <li>Must be availed within a reasonable period from the date of birth or legal adoption.</li>
              <li>Supporting documentation — birth certificate, hospital records, or adoption order — is required prior to or within 3 working days of taking the leave.</li>
            </ol>
          </PolicySection>

          <PolicySection title="7. Bereavement Leave (BL)">
            <ol className={styles.policyList}>
              <li><strong>Carry-Forward:</strong> Bereavement Leave does <strong>NOT carry forward</strong>. It is a per-event entitlement; unused BL in any given year lapses automatically at year-end.</li>
              <li><strong>On Leaving the Company:</strong> BL <strong>lapses on separation</strong> — it is not encashable and holds no monetary value upon resignation, termination, or retirement.</li>
              <li>Applicable only upon the demise of an <strong>immediate family member</strong>, defined as: spouse, child, parent, sibling, or parent-in-law.</li>
              <li>A copy of the <strong>death certificate or equivalent official documentation</strong> must be submitted to HR within <strong>7 working days</strong> of return to duty.</li>
              <li>Leave in connection with the demise of relatives outside the above definition shall be covered under Casual Leave or Earned Leave, subject to availability.</li>
              <li>Additional bereavement leave beyond the entitled days shall be treated as Earned Leave or Unpaid Leave, subject to management approval.</li>
            </ol>
          </PolicySection>

          <PolicySection title="8. Unpaid Leave (UL) — Detailed Terms" highlight>
            <div className={styles.policyAlert}>
              ⚠ Unpaid Leave results in a <strong>salary deduction</strong> computed as: <em>Basic Salary ÷ 26 × Number of days absent</em>.
              Prior written approval is mandatory. This is not a statutory entitlement but a discretionary facility subject to management approval.
            </div>
            <ol className={styles.policyList}>
              <li>Unpaid Leave is not a statutory entitlement but a <strong>discretionary facility</strong> extended by the Company on a case-by-case basis.</li>
              <li><strong>Prior written approval</strong> from the immediate manager and HR is mandatory before commencing Unpaid Leave, except in genuine emergencies where communication must be made at the earliest practicable moment and regularised upon return.</li>
              <li>Salary deduction shall be: <strong>Basic Salary ÷ 26 × number of days</strong>. The deduction will be reflected in the payroll for the relevant month. (26 = standard working-day multiplier per applicable labour conventions.)</li>
              <li>The following supporting documents are required based on the reason category:</li>
            </ol>

            <div className={styles.proofTableWrap}>
              <table className={styles.proofTable}>
                <thead>
                  <tr>
                    <th>Reason Category</th>
                    <th>Required Documentation</th>
                    <th>Submission Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Medical / Health Condition</strong></td>
                    <td>Medical certificate from a Registered Medical Practitioner; hospitalization records if admitted</td>
                    <td>Within 3 working days of return</td>
                  </tr>
                  <tr>
                    <td><strong>Family Medical Emergency</strong></td>
                    <td>Doctor's certificate for the affected family member; hospital admission / discharge records</td>
                    <td>Within 3 working days of return</td>
                  </tr>
                  <tr>
                    <td><strong>Personal Emergency</strong> (Accident, Natural Disaster, etc.)</td>
                    <td>FIR / Police Report / official government authority certificate</td>
                    <td>Within 3 working days of return</td>
                  </tr>
                  <tr>
                    <td><strong>Bereavement — Extended</strong> (beyond BL entitlement)</td>
                    <td>Death certificate; proof of relationship with the deceased</td>
                    <td>Within 3 working days of return</td>
                  </tr>
                  <tr>
                    <td><strong>Personal / Compelling Personal Reasons</strong></td>
                    <td>Written self-declaration; accepted subject to management discretion</td>
                    <td>At the time of application</td>
                  </tr>
                  <tr>
                    <td><strong>Other</strong></td>
                    <td>Supporting official document as specified by HR at the time of application</td>
                    <td>As directed by HR</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <ol className={styles.policyList} start={5}>
              <li>All supporting documents must be submitted to HR within <strong>3 working days of resumption of duty</strong>. Non-submission may result in the absence being reclassified as <strong>Absence Without Leave (AWOL)</strong>, which constitutes misconduct under the Company's disciplinary framework.</li>
              <li>Unpaid Leave during the <strong>probation period</strong> shall ordinarily not be approved except in extraordinary circumstances at the sole discretion of HR &amp; Management.</li>
              <li>Repeated applications for Unpaid Leave without adequate justification may be flagged in the employee's attendance record and taken into account in performance and appraisal assessments.</li>
              <li>The Company's decision to approve or reject an Unpaid Leave application is final and binding, subject always to rights and protections available to the employee under applicable law.</li>
              <li>Approved Unpaid Leave does not interrupt continuity of service for the purpose of statutory benefits, unless otherwise prescribed by applicable law.</li>
            </ol>
          </PolicySection>

          <PolicySection title="9. Loss of Pay (LOP) &amp; Absence Without Leave (AWOL)">
            <ol className={styles.policyList}>
              <li>Any absence not covered by approved leave shall be treated as <strong>Loss of Pay (LOP)</strong>. Repeated or extended LOP may attract disciplinary action.</li>
              <li>Absence Without Leave (AWOL) for <strong>3 or more consecutive working days</strong> without communication may be treated as voluntary abandonment of employment unless the employee provides satisfactory justification within 48 hours of being contacted.</li>
              <li>LOP deductions shall be processed in the payroll of the relevant month and shall appear on the salary slip.</li>
            </ol>
          </PolicySection>

          <PolicySection title="10. General Obligations">
            <ol className={styles.policyList}>
              <li>Leave cannot be clubbed with public holidays or weekly offs to create extended breaks without prior management approval.</li>
              <li>The Company reserves the right to recall an employee from approved leave in exceptional business circumstances, with reasonable advance notice.</li>
              <li>No leave shall be granted or availed in a manner that violates any law, rule, or regulation applicable to the employment relationship.</li>
              <li>This policy shall be construed and applied in a manner consistent with applicable law. In the event of any conflict between this policy and applicable law, <strong>applicable law shall prevail</strong>.</li>
              <li>This policy may be revised by the Company at any time to ensure continued compliance with applicable law, with reasonable notice to employees.</li>
            </ol>
          </PolicySection>

          <PolicySection title="11. Employee Separation — Leave Encashment &amp; Settlement">
            <div className={styles.policyAlert} style={{ borderColor: "#93c5fd", background: "#eff6ff", color: "#1e3a5f" }}>
              ℹ On separation (resignation, termination, retirement, or death), <strong>Earned Leave</strong> encashment is a statutory right. All other leave types lapse. Settlement is processed in the final payroll cycle.
            </div>
            <ol className={styles.policyList}>
              <li><strong>EL/PL — Mandatory Encashment:</strong> All accumulated Earned Leave (up to the 30-day cap) outstanding at the date of separation shall be compulsorily encashed. The encashment rate is: <em>Basic Salary ÷ 26 × unutilised EL days</em>. This right cannot be contractually waived or forfeited, except where permissible under applicable law (e.g., termination for proven misconduct in states where statute permits forfeiture).</li>
              <li><strong>CL at Separation:</strong> Unused Casual Leave <strong>lapses on the last working day</strong>. The Company is not obligated to compensate for unused CL on separation.</li>
              <li><strong>SL at Separation:</strong> Unused Sick Leave <strong>lapses on the last working day</strong> and is <strong>not encashable</strong> on separation except where the applicable state law of the place of employment mandates otherwise.</li>
              <li><strong>ML/PL/BL at Separation:</strong> These are per-event entitlements with no balance accumulation. There is nothing to encash.</li>
              <li><strong>Notice Period &amp; Leave Clubbing:</strong> An employee who has tendered resignation may not, without written management approval, utilise EL, CL, or SL during the notice period to shorten the effective working notice rendered. The Company reserves the right to reject such leave applications during notice period in the interest of orderly handover, subject always to applicable law.</li>
              <li><strong>Short Service (less than 12 months):</strong> EL accrual shall be pro-rated for the months actually worked after confirmation. Employees who leave before completing one year of confirmed service shall be entitled only to EL accrued till date of separation.</li>
              <li><strong>Resignation without notice / abandonment:</strong> Where an employee leaves without serving the required notice period, the Company may adjust the leave encashment amount against any notice period dues payable, to the extent permissible under applicable law.</li>
              <li><strong>Settlement timeline:</strong> Leave encashment shall be processed in the <strong>final full-month payroll cycle</strong> following the employee's last working day, ordinarily within <strong>30 days of the last working day</strong> or within such timeline as required by applicable law.</li>
            </ol>
          </PolicySection>

          <PolicySection title="12. Company-Reserved Protective Clauses (Operational Safeguards)">
            <div className={styles.policyAlert} style={{ borderColor: "#c4b5fd", background: "#f5f3ff", color: "#3b0764" }}>
              ℹ The following provisions are exercised by the Company within the limits of applicable law to ensure operational continuity, prevent abuse, and manage leave liability responsibly.
            </div>
            <ol className={styles.policyList}>
              <li><strong>Sandwich Rule:</strong> Any intervening weekly-off days, declared public holidays, or company holidays that fall <em>between</em> two approved leave days shall be counted as leave days (i.e., included in the leave duration). For example, if leave is taken on Friday and Monday, Saturday and Sunday are also counted, making it 4 leave days. This applies to EL, CL, and SL.</li>
              <li><strong>EL Carry-Forward Cap (Liability Management):</strong> The Company caps the maximum EL carry-forward at <strong>30 days</strong>. EL accumulated beyond this cap at year-end lapses without encashment. Employees are notified in advance to utilise excess leave. This cap is in accordance with the applicable labour law framework and limits the Company's contingent encashment liability.</li>
              <li><strong>No Mid-Service EL Encashment:</strong> Earned Leave may not be encashed while in active employment. Leave must be taken as rest. This prevents premature liquidation of the Company's statutory leave obligation.</li>
              <li><strong>Peak Period / Blackout Restriction:</strong> The Company may designate specific periods as <strong>Restricted Leave Periods</strong> (e.g., financial year-end, audit periods, product launch cycles, examination or training seasons) during which EL may be deferred. Advance notice of at least <strong>2 weeks</strong> shall be provided. This does not affect an employee's statutory right to leave — only the timing.</li>
              <li><strong>Maximum Continuous EL:</strong> An employee may not avail more than <strong>10 consecutive days of EL</strong> at one time without specific written management approval. Requests exceeding this limit require at least <strong>21 calendar days' advance notice</strong> and are subject to operational feasibility.</li>
              <li><strong>Probation Period — Full Restriction (3 Months):</strong> No leave of any kind (EL, CL, SL, or any other type) accrues or may be availed during the Probation Period (first 3 calendar months from joining, or 6 months if extended by Superadmin). The leave application system is automatically locked for employees in probation. See Section 2.6 for the complete policy including the emergency exception, LWP treatment, and probation extension rules.</li>
              <li><strong>Block Leave Utilisation:</strong> To promote genuine rest, EL must be availed in minimum blocks of <strong>2 consecutive days</strong>. Single-day EL applications are ordinarily not permitted except in exceptional personal circumstances subject to manager approval.</li>
              <li><strong>Leave Approval Hierarchy:</strong> All leave applications must be approved through the designated system. Leaves communicated only verbally or via personal messaging apps are not valid and will be treated as LOP until formal system approval is granted.</li>
              <li><strong>Right to Recall:</strong> The Company reserves the right to recall an employee from any approved leave (except ML and ESIC-supported SL) in a genuine operational emergency. The Company shall compensate for reasonable travel costs arising from such recall and ensure the cancelled leave is rescheduled at a mutually convenient time.</li>
              <li><strong>Leave Balance Correction:</strong> HR reserves the right to correct errors in leave balances (over-credits, system errors) with retrospective effect after giving the employee reasonable notice. Amounts already over-drawn against an incorrect balance may be adjusted from future earned leave or final settlement.</li>
              <li><strong>Absence During Notice Period:</strong> During the notice period following resignation, the Company may decline leave applications except for emergencies, to ensure proper knowledge transfer and handover. Medical leave supported by a valid certificate shall not be declined but may extend the notice period by an equivalent number of days, subject to applicable law.</li>
              <li><strong>Year-End Lapse Notice:</strong> The Company shall send a leave-balance reminder to all employees at least <strong>45 days before year-end</strong> to enable timely utilisation of leave that would otherwise lapse (CL; EL beyond the 30-day cap). Failure to send this notice will not, however, create any additional monetary obligation beyond what is mandated by applicable statute.</li>
            </ol>
          </PolicySection>

          <PolicySection title="13. General Obligations">
            <ol className={styles.policyList}>
              <li>Leave cannot be clubbed with public holidays or weekly offs to create extended breaks without prior management approval.</li>
              <li>No leave shall be granted or availed in a manner that violates any law, rule, or regulation applicable to the employment relationship.</li>
              <li>This policy shall be construed and applied in a manner consistent with applicable law. <strong>In the event of any conflict between this policy and applicable law, applicable law shall prevail.</strong></li>
              <li>This policy may be revised by the Company at any time. Revisions shall be communicated to employees with reasonable advance notice and shall always comply with applicable law.</li>
              <li>All disputes arising out of or in connection with this leave policy shall be resolved through the Company's internal grievance mechanism in the first instance, and thereafter in accordance with applicable dispute resolution law.</li>
            </ol>
          </PolicySection>

          <p className={styles.policyFootnote}>
            Subject to Company approval · Compliant with applicable central and state labour laws in force · Last reviewed {currentYear}
            <br />
            EL encashment on separation is a statutory right and is non-waivable · CL &amp; SL are not encashable on separation · EL carry-forward capped at 30 days
          </p>
        </div>
      )}
    </div>
  );
};

export default Leave;